import { useState, useEffect, useCallback } from 'react';
import { ToggleButton } from '../toggleButton';
import { BreedDropdown } from '../BreedDropdown';
import { SelectInput } from '../select';
import { FormImage } from '../utils/svg-icon';
import { AppConstant } from '../utils/app-constant';
import axios from '../../custom-axios';
import './widget.css';
import { AppUtils } from '../utils/app-utils';
import { Quote } from '../quote';
import { useMCPBeaconScript } from '../../hooks/useMCPBeaconScript';
import { PRIVACY_PPOLICY_LINK, TERMS_OF_USE_LINK } from '../../constants';
import { usePartnerData } from '../../hooks/usePartnerData';

export const Widget = () => {
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);
  const partnerGuid = params.get('partnerGuid');
  const { logo } = usePartnerData(partnerGuid);
  const [petName, setPetName] = useState('');
  const [selectedPet, setSelectedPet] = useState('Dog');
  const [selectedGender, setSelectGender] = useState('Female');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedBreed, setSelectedBreed] = useState('');
  const [isChecked, setIsChecked] = useState(false);
  const [showCheckboxError, setShowCheckboxError] = useState(false);
  const [zipCode, setZipCode] = useState('');
  const [email, setEmail] = useState('');
  const [showLoader, setShowLoader] = useState(false);
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [isValidZip, setIsValidZip] = useState(true);
  const [isValidBreed, setIsValidBreed] = useState(true);
  const [isValidAge, setIsValidAge] = useState(true);
  const [isValidPetName, setIsValidPetName] = useState(true);
  const [quoteInfo, setQuoteInfo] = useState(null);
  const [utmSource, setUtmSource] = useState('');
  const [utmMedium, setUtmMedium] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [groupCodeDscr, setGroupCodeDscr] = useState('');
  const [groupCode, setGroupCode] = useState('');
  useMCPBeaconScript();

  const handleToggleValue = (event, type) => {
    event.preventDefault();
    setSelectedBreed('');
    if (type.toLowerCase() === 'gender') {
      setSelectGender(event.target.name);
      return;
    }

    setSelectedPet(event.target.name);
  }

  const getButtonState = () => {
    const isEmailRequired = email !== '';
    const isPetNameRequired = petName !== '';
    const isPostalCodeRequired = zipCode !== '';
    const isPetBreedSelected = !!selectedBreed;
    const isPetAgeSelected = !!selectedYear;

    if (isEmailRequired &&
      isPetAgeSelected &&
      isPetBreedSelected &&
      isPostalCodeRequired &&
      isPetNameRequired &&
      isValidEmail &&
      isValidZip &&
      isChecked
    ) {
      return false;
    }

    return true;
  }

  const getBtnClass = () => {
    if (getButtonState()) {
      return 'ui-form-btn ui-form-btn--disabled';
    }

    return 'ui-form-btn';
  }

  const getCheckboxText = () => {
    const links = (
      <>
        <a href={TERMS_OF_USE_LINK} rel="noopener noreferrer" target='_blank'>Terms of Use</a>
        &nbsp;and&nbsp;
        <a href={PRIVACY_PPOLICY_LINK} rel="noopener noreferrer" target='_blank'>Privacy Policy</a>
      </>
    )

    if (partnerGuid) {
      return (
        <p>
          The information you provide is being submitted to Figo. By checking this box,&nbsp;
          you confirm that I accept Figo’s&nbsp;
          {links}
        </p>
      )
    }
    return (
      <p>
        By checking this box, I confirm that I accept Figo’s&nbsp;
        {links}
      </p>
    )
  }

  const submitHandler = async (e) => {
    e.preventDefault();
    if (showLoader) {
      return;
    }

    if (!isChecked) {
      showCheckboxError(true);
      return;
    }

    setShowLoader(true);

    const promoCode = params.get('groupCode');
    const breedId = selectedBreed.Id;
    const birthDate = AppUtils.getDOB(parseInt(selectedYear.value));

    try {
      const headers = {
        'Api-Version': `${process.env.REACT_APP_API_VERSION}`,
        'Ocp-Apim-Subscription-Key': `${process.env.REACT_APP_SUBSCRIPTION_KEY}`,
        'Content-Type': 'application/json',
      }

      const response = await axios.processGet(
        `${process.env.REACT_APP_API_BASE_URL}/widget/rate?Name=${petName}&BirthDate=${birthDate}&breedId=${breedId}`
        + `&zipCode=${zipCode}&promoCode=${promoCode ?? ''}&customerEmail=${email}&gender=${AppConstant.GENDER[selectedGender.toLowerCase()]}`
        + `${partnerGuid ? `&partnerGuid=${partnerGuid}` : ''}`,
        { headers }
      )

      if (response?.data?.HasMessages && response?.data?.Message?.toLowerCase().includes('zip')) {
        setIsValidZip(false);
        return;
      }
      setQuoteInfo(response?.data?.Data);
    } catch { }
    finally {
      setShowLoader(false);
    }
  }

  const handleBreedChange = (value) => {
    setSelectedBreed(value)
    setIsValidBreed(!!value)
  }
  const handleAgeChange = (value) => {
    setSelectedYear(value)
    setIsValidAge(!!value)
  }

  const handleEmailBlurEvent = () => {
    setIsValidEmail(AppUtils.validateEmail(email));
  }

  const handleZipCodeBlurEvent = () => {
    setIsValidZip(AppUtils.validateZipCode(zipCode));
  }

  const handleCheckboxBlur = () => {
    setShowCheckboxError(!isChecked);
  }

  const handlePetNameBlur = useCallback(() => {
    setIsValidPetName(!!petName.length)
  }, [petName]);

  const handleBreedBlur = useCallback(() => {
    setIsValidBreed(!!selectedBreed)
  }, [selectedBreed]);

  const handleAgeBlur = useCallback(() => {
    setIsValidAge(!!selectedYear)
  }, [selectedYear]);

  useEffect(() => {
    if (isChecked) {
      setShowCheckboxError(false);
    }
  }, [isChecked]);

  const sendPostMessage = useCallback(() => {
    try {
      const mainElement = document.getElementsByTagName('main')[0];
      window.parent.postMessage({
        source: 'quote-widget',
        height: getComputedStyle(mainElement).getPropertyValue('height'),
        width: getComputedStyle(mainElement).getPropertyValue('width'),
      }, '*');
    } catch (error) {
      // do nothing
    }
  }, []);

  useEffect(() => {
    window.addEventListener('resize', sendPostMessage);
    sendPostMessage();

    return () => {
      window.removeEventListener('resize', sendPostMessage);
    };
  }, [sendPostMessage]);

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    if (params?.utm_source) {
      setUtmSource(params.utm_source);
    }
    if (params?.utm_medium) {
      setUtmMedium(params.utm_medium);
    }
    if (params?.utm_campaign) {
      setUtmCampaign(params.utm_campaign);
    }
    if (params?.groupCode) {
      setGroupCode(params.groupCode);
    }
    if (params?.groupCodeDscr) {
      setGroupCodeDscr(params.groupCodeDscr);
    }
  }, [])

  return (
    <>
      {
        !quoteInfo
          ?
          <main
            className={'ui-widget-container'
              + `${logo ? ' ui-widget-container-partner' : ''}`
            }
          >
            {logo && <img alt="costco" className="ui-partner-logo" src={logo} />}
            <div className='ui-widget-text' data-ispartner={!!logo}>
              <p className='heading'>Customized <span>by you</span></p>
              <p className='info'>Design your pet’s plan in less than 60 seconds</p>
              {!logo && <FormImage />}
            </div>

            <form className='ui-widget-form' onSubmit={submitHandler}>
              <div className='ui-form-row-input'>
                <label className={`ui-input-label ${!isValidPetName ? 'ui-input-error-message' : ''}`}>Pet Name</label>
                <input
                  className='ui-form-input'
                  type='text'
                  name='petName'
                  value={petName}
                  onChange={(event) => setPetName(event.target.value)}
                  onBlur={handlePetNameBlur}
                  placeholder='Enter pet name'
                />
                {!isValidPetName && <span className='ui-input-error-message'>Please enter your pet's name</span>}
              </div>
              <div className="ui-form-toggle-row">
                <ToggleButton type='Species' items={['Cat', 'Dog']} selectedValue={selectedPet} toggleValue={handleToggleValue} />
                <hr></hr>
                <ToggleButton type='Gender' items={['Male', 'Female']} selectedValue={selectedGender} toggleValue={handleToggleValue} />
              </div>
              <div className="ui-form-select-row">
                <div>
                  <label className={`ui-input-label ${!isValidBreed ? 'ui-input-error-message' : ''}`}>Breed</label>
                  <BreedDropdown
                    value={selectedBreed}
                    type={selectedPet.toLocaleLowerCase() === 'dog' ? 0 : 1}
                    handleBreedChange={handleBreedChange}
                    handleBreedBlur={handleBreedBlur}
                    isValid={isValidBreed}
                  />
                  {!isValidBreed && <span className='ui-input-error-message'>Please select your pet's breed</span>}
                </div>
                <div>
                  <label className={`ui-input-label ${!isValidAge ? 'ui-input-error-message' : ''}`}>Age (years)</label>
                  <SelectInput
                    type='text'
                    value={selectedYear}
                    handleChange={handleAgeChange}
                    handleBlur={handleAgeBlur}
                    options={AppConstant.AGE_YEARS}
                    isSearchable={true}
                    placeholder={'Select age'}
                  />
                  {!isValidAge && <span className='ui-input-error-message'>Please select your pet's age</span>}
                </div>
              </div>
              <div className="ui-form-row">
                <div className='ui-form-row-input'>
                  <label className={`ui-input-label ${!isValidZip ? 'ui-input-error-message' : ''}`}>Zip Code</label>
                  <input
                    className={'ui-form-input ' + (isValidZip ? 'ui-form-input-error' : '')}
                    type='text'
                    name='postalCode'
                    value={zipCode}
                    onChange={(event) => setZipCode(event.target.value)}
                    onBlur={handleZipCodeBlurEvent}
                    placeholder='Zip Code'
                  ></input>
                  {!isValidZip && <span className='ui-input-error-message'>Invalid zip code</span>}
                </div>
                <div className='ui-form-row-input'>
                  <label className={`ui-input-label ${!isValidEmail ? 'ui-input-error-message' : ''}`}>Your Email</label>
                  <input
                    className={'ui-form-input ' + (isValidEmail ? 'ui-form-input-error' : '')}
                    type='text'
                    name='email'
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    onBlur={handleEmailBlurEvent}
                    placeholder='Enter your email'
                  ></input>
                  {!isValidEmail && <span className='ui-input-error-message'>Invalid email</span>}
                </div>
              </div>
              <div className='ui-form-checkbox-wrapper'>
                <label data-error={showCheckboxError}>
                  <input type='checkbox' checked={isChecked} onChange={() => setIsChecked((prev) => !prev)} onBlur={handleCheckboxBlur}></input>
                  <div className="checkmark" />
                  {getCheckboxText()}
                </label>
              </div>
              <div className='ui-form-btn-wrapper'>
                <button className={getBtnClass()} disabled={getButtonState()}>
                  {showLoader ? 'Loading...' : 'Get your free quote'}
                </button>
              </div>
            </form>
          </main>
          :
          <Quote
            quoteInfo={quoteInfo}
            petType={selectedPet}
            gender={selectedGender}
            email={email}
            zipCode={zipCode}
            utmSource={utmSource}
            utmMedium={utmMedium}
            utmCampaign={utmCampaign}
            groupCode={groupCode}
            groupCodeDscr={groupCodeDscr}
            partnerGuid={partnerGuid}
            logo={logo}
          />
      }
    </>
  )
}

